import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { DoorOpenIcon, MoreHorizontalIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import type { Room } from '#admin/features/program/api.ts'
import { useDeleteRoom } from '#admin/features/program/mutations.ts'
import { roomsQuery } from '#admin/features/program/queries.ts'
import { RoomDialog } from '#admin/features/program/room-dialog.tsx'
import { RoomsSkeleton } from '#admin/features/program/program-skeletons.tsx'

/**
 * The venue's rooms and the editor for them, gated on `room`. Its own page so neither it nor the
 * agenda outgrows one table. Without `room.manage` the write controls are removed.
 *
 * Delete is refused while sessions or agreed meetings still use the room; the toast names which.
 */
export const Route = createFileRoute('/_authenticated/program/rooms')({
  pendingComponent: RoomsSkeleton,
  staticData: { crumb: { parents: [{ label: 'Program' }], label: 'Rooms' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(roomsQuery()),
      context.queryClient.ensureQueryData(sessionQuery()),
    ]),
  component: RoomsPage,
})

function RoomsPage() {
  const { data: rooms } = useSuspenseQuery(roomsQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'room.manage')

  const [editing, setEditing] = useState<{ room: Room | null } | null>(null)
  const [deleting, setDeleting] = useState<Room | null>(null)
  const remove = useDeleteRoom(() => setDeleting(null))

  const columns = useMemo<ColumnDef<Room, never>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      {
        accessorKey: 'name',
        header: 'Room',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            {row.original.name}
            {row.original.isActive ? null : (
              <StatusBadge tone="neutral">out of service</StatusBadge>
            )}
          </span>
        ),
      },
      {
        id: 'capacity',
        header: 'Seats',
        meta: { align: 'end' },
        cell: ({ row }) => row.original.capacity ?? '—',
      },
      {
        // Two capacities, because they count different things: seats for an audience, and
        // concurrent brokered meetings.
        accessorKey: 'meetingCapacity',
        header: 'Meetings at once',
        meta: { align: 'end' },
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const },
              cell: ({ row }: { row: { original: Room } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${row.original.name}`}
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing({ room: row.original })}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(row.original)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies ColumnDef<Room, never>,
          ]
        : []),
    ],
    [canManage],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Rooms"
        description={`${rooms.items.length} room${rooms.items.length === 1 ? '' : 's'} at the venue`}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ room: null })}>
              <PlusIcon />
              Add room
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the venue" />}

      <DataTable
        caption="Venue rooms"
        columns={columns}
        data={rooms.items}
        getRowId={(room) => room.id}
        empty={
          <EmptyState
            icon={<DoorOpenIcon />}
            title="No rooms yet"
            description="A session cannot be published until it has a room, so this is the place to start."
          />
        }
      />

      {editing === null ? null : (
        <RoomDialog
          // Keyed, so opening another room starts from its own values rather than the last edit.
          key={editing.room?.id ?? 'new'}
          room={editing.room}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={`Delete ${deleting?.name ?? ''}?`}
        description="Refused while sessions are placed in it or meetings are agreed there. To stop using it for now, take it out of service instead."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => (deleting === null ? undefined : remove.mutate(deleting))}
      />
    </div>
  )
}
